import { useMemo } from "react";
import { useSubs } from "@/context/SubsContext";
import { buildRecommendations, formatEUR } from "@/lib/subscriptions";
import { Badge } from "@/components/ui/badge";
import { Sparkles, AlertCircle, TrendingDown, CalendarCheck, HelpCircle } from "lucide-react";

const TYPE_META = {
  doublon: { icon: AlertCircle, color: "bg-amber-100 text-amber-800" },
  cher: { icon: TrendingDown, color: "bg-emerald-100 text-emerald-800" },
  fréquence: { icon: CalendarCheck, color: "bg-sky-100 text-sky-800" },
  usage: { icon: HelpCircle, color: "bg-gray-100 text-gray-700" },
};

export default function Savings() {
  const { subs, loading } = useSubs();

  const recs = useMemo(() => buildRecommendations(subs), [subs]);
  const totalPotential = useMemo(
    () => recs.filter((r) => Number.isFinite(r.savingsMonthly)).reduce((s, r) => s + r.savingsMonthly, 0),
    [recs]
  );

  if (loading) {
    return <div className="text-gray-500 py-10 text-center">Analyse en cours…</div>;
  }

  return (
    <div className="space-y-6" data-testid="savings-root">
      {/* Hero */}
      <section
        className="rounded-2xl p-8 bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-700 text-white shadow-soft-lg overflow-hidden relative"
        data-testid="savings-hero"
      >
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,#34d399_0%,transparent_60%)]" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-emerald-200 text-[11px] font-bold uppercase tracking-[0.2em] mb-3">
            <Sparkles size={14} /> Assistant intelligent
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            {recs.length} recommandation{recs.length > 1 ? "s" : ""} à votre portée
          </h2>
          <p className="text-emerald-100 max-w-2xl text-sm sm:text-base">
            Analyse basée uniquement sur vos abonnements enregistrés. Les économies affichées sont des estimations.
            Avant toute décision, vérifiez les conditions auprès de votre fournisseur.
          </p>
          <div className="mt-6 inline-flex items-baseline gap-3 bg-white/10 backdrop-blur rounded-2xl px-5 py-4 border border-white/15">
            <span className="text-emerald-200 text-xs uppercase tracking-wider">Potentiel total</span>
            <span className="font-heading text-3xl font-bold" data-testid="total-potential-savings">
              {formatEUR(totalPotential)}
            </span>
            <span className="text-emerald-100 text-sm">/ mois</span>
          </div>
        </div>
      </section>

      {recs.length === 0 ? (
        <div className="rounded-2xl bg-white p-10 border border-gray-100 shadow-soft text-center" data-testid="savings-empty">
          <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">Aucune recommandation</h3>
          <p className="text-gray-500">Ajoutez quelques abonnements pour obtenir une analyse personnalisée.</p>
        </div>
      ) : (
        <ul className="space-y-4" data-testid="savings-list">
          {recs.map((r) => {
            const meta = TYPE_META[r.type] || TYPE_META.usage;
            const Icon = meta.icon;
            return (
              <li
                key={r.id}
                data-testid={`rec-${r.id}`}
                className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft card-hover"
              >
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} strokeWidth={1.75} className="text-emerald-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge className={`rounded-full capitalize ${meta.color}`}>{r.type}</Badge>
                      <h3 className="font-heading text-lg font-bold text-gray-900">{r.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{r.description}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                      <div className="rounded-xl bg-gray-50 px-4 py-3">
                        <div className="text-gray-500">Prix actuel</div>
                        <div className="font-semibold text-gray-900 text-sm">
                          {Number.isFinite(r.currentMonthly) ? formatEUR(r.currentMonthly) + " /mois" : "—"}
                        </div>
                      </div>
                      <div className="rounded-xl bg-gray-50 px-4 py-3">
                        <div className="text-gray-500">Prix alternatif</div>
                        <div className="font-semibold text-gray-900 text-sm">
                          {Number.isFinite(r.alternativeMonthly)
                            ? formatEUR(r.alternativeMonthly) + " /mois"
                            : "À renseigner"}
                        </div>
                      </div>
                      <div className="rounded-xl bg-emerald-50 px-4 py-3">
                        <div className="text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
                          Économie estimée
                        </div>
                        <div className="font-semibold text-emerald-800 text-sm">
                          {Number.isFinite(r.savingsMonthly) && r.savingsMonthly > 0
                            ? `${formatEUR(r.savingsMonthly)} /mois · ${formatEUR(r.savingsMonthly * 12)} /an`
                            : "À chiffrer"}
                        </div>
                      </div>
                    </div>
                    {r.condition && (
                      <div className="mt-3 text-xs text-gray-500 italic border-l-2 border-emerald-200 pl-3">
                        {r.condition}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
